import { useRouteLoaderData } from '@remix-run/react';

import type { loader as communityLoader } from '~/routes/community';
import { COMMUNITY_DESTINATION_PARAM } from '~/utils/buildPostAuthRedirectUrl';

import { JOIN_INTENT_URL, SUCCESS_REDIRECT_TO } from '../_components/CommunityUpgradeModal/constants';
import { communityInviteModalUrl } from '../_utils/communityInviteModalUrl';

export interface CommunityUpgradeReturn {
  successRedirectTo: string;
  // Same landing without `upgrade_return`: a sign-in that paid nothing must not wait for an entitlement.
  signInRedirectTo: string;
  // The invite modal is already back on screen once access lands and owns the acceptance,
  // so the post-purchase handoff must not reopen the join intent over it.
  resumesInviteAcceptance: boolean;
}

/**
 * Where a community upgrade lands once paid: back on the join intent, or, when the
 * viewer arrived through an invite link, back on the tokenized invite url with the
 * invited address as the checkout's starting point. Both carry `upgrade_return=1`
 * so the landing surface knows to wait for the entitlement rather than sell again.
 * The sign-in return lands on the same surface without the flag.
 */
export const useCommunityUpgradeReturn = (destination: string | null): CommunityUpgradeReturn => {
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const invite = layoutData?.invite;

  if (invite?.state !== 'accept_ready') {
    const destinationParam = destination ? `&${COMMUNITY_DESTINATION_PARAM}=${encodeURIComponent(destination)}` : '';

    return {
      successRedirectTo: `${SUCCESS_REDIRECT_TO}${destinationParam}`,
      signInRedirectTo: `${JOIN_INTENT_URL}${destinationParam}`,
      resumesInviteAcceptance: false,
    };
  }

  const inviteUrl = communityInviteModalUrl(invite.tokenId, invite.token);

  return {
    successRedirectTo: `${inviteUrl}&upgrade_return=1`,
    signInRedirectTo: inviteUrl,
    resumesInviteAcceptance: true,
  };
};
